/**
 * Footer Component
 *
 * Site footer with quick links, social media, and newsletter
 * Task: T4.2.2 - Footer component
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Text,
  BlockStack,
  InlineStack,
  InlineGrid,
  TextField,
  Button,
  Divider,
} from '@shopify/polaris';
import { useSettingsStore } from '../../store/settingsStore';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterProps {
  companyDescription?: string;
  quickLinks?: FooterLink[];
  helpLinks?: FooterLink[];
  policyLinks?: FooterLink[];
  socialLinks?: {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
    instagram?: string;
  };
  showNewsletter?: boolean;
}

const defaultQuickLinks: FooterLink[] = [
  { label: 'Home', href: '/' },
  { label: 'Categories', href: '/categories' },
  { label: 'About Us', href: '/about' },
  { label: 'Contact', href: '/contact' },
];

const defaultHelpLinks: FooterLink[] = [
  { label: 'FAQ', href: '/faq' },
  { label: 'Catalogue', href: '/catalogue' },
  { label: 'Request Sample', href: '/contact' },
  { label: 'Bulk Orders', href: '/contact' },
];

const defaultPolicyLinks: FooterLink[] = [
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms & Conditions', href: '/terms' },
  { label: 'Shipping & Delivery', href: '/shipping' },
  { label: 'Cancellation & Refund', href: '/refund' },
];

export function Footer({
  companyDescription = 'Your trusted partner for premium corporate gifting solutions. We help businesses create memorable impressions with customized gifts.',
  quickLinks = defaultQuickLinks,
  helpLinks = defaultHelpLinks,
  policyLinks = defaultPolicyLinks,
  socialLinks = {},
  showNewsletter = true,
}: FooterProps) {
  // Get settings from store
  const { company, fetchSettings } = useSettingsStore();

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState('');

  const handleNewsletterSubmit = async () => {
    if (!email) return;

    setSubscribeLoading(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setSubscribeMessage('Thank you for subscribing!');
    setEmail('');
    setSubscribeLoading(false);

    // Clear message after 3 seconds
    setTimeout(() => setSubscribeMessage(''), 3000);
  };

  const handleLinkClick = (href: string) => {
    navigate(href);
  };

  const currentYear = new Date().getFullYear();

  return (
    <Box
      background="bg-surface-secondary"
      padding="800"
      borderBlockStartWidth="025"
      borderColor="border"
    >
      <BlockStack gap="800">
        {/* Main Footer Content */}
        <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="600">
          {/* Company Info */}
          <BlockStack gap="400">
            <img
              src="/footer_logo.png"
              alt="Gift Sangam - Samjhauta nahin, Sangam Hai"
              style={{ height: '140px', width: 'auto', alignSelf: 'flex-start' }}
            />
            <Text as="p" variant="bodySm" tone="subdued">
              {companyDescription}
            </Text>

            {/* Social Links */}
            <InlineStack gap="300">
              {socialLinks.facebook && (
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <SocialIcon type="facebook" />
                </a>
              )}
              {socialLinks.twitter && (
                <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <SocialIcon type="twitter" />
                </a>
              )}
              {socialLinks.linkedin && (
                <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <SocialIcon type="linkedin" />
                </a>
              )}
              {socialLinks.instagram && (
                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <SocialIcon type="instagram" />
                </a>
              )}
            </InlineStack>
          </BlockStack>

          {/* Quick Links */}
          <BlockStack gap="300">
            <Text as="h3" variant="headingMd" fontWeight="semibold">
              Quick Links
            </Text>
            {quickLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleLinkClick(link.href)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <Text as="span" variant="bodySm" tone="subdued">
                  {link.label}
                </Text>
              </button>
            ))}
          </BlockStack>

          {/* Help Links */}
          <BlockStack gap="300">
            <Text as="h3" variant="headingMd" fontWeight="semibold">
              Help
            </Text>
            {helpLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleLinkClick(link.href)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <Text as="span" variant="bodySm" tone="subdued">
                  {link.label}
                </Text>
              </button>
            ))}
          </BlockStack>

          {/* Contact & Newsletter */}
          <BlockStack gap="400">
            <Text as="h3" variant="headingMd" fontWeight="semibold">
              Contact Us
            </Text>

            {company.email && (
              <Text as="p" variant="bodySm" tone="subdued">
                Email: {company.email}
              </Text>
            )}
            {company.phone && (
              <Text as="p" variant="bodySm" tone="subdued">
                Phone: {company.phone}
              </Text>
            )}
            {company.address && (
              <Text as="p" variant="bodySm" tone="subdued">
                {company.address}
              </Text>
            )}

            {showNewsletter && (
              <BlockStack gap="200">
                <Text as="p" variant="bodySm" fontWeight="semibold">
                  Subscribe to Newsletter
                </Text>
                <BlockStack gap="200">
                  <TextField
                    label="Email"
                    labelHidden
                    type="email"
                    value={email}
                    onChange={setEmail}
                    placeholder="Enter your email"
                    autoComplete="email"
                  />
                  <Button
                    variant="primary"
                    onClick={handleNewsletterSubmit}
                    loading={subscribeLoading}
                  >
                    Subscribe
                  </Button>
                </BlockStack>
                {subscribeMessage && (
                  <Text as="p" variant="bodySm" tone="success">
                    {subscribeMessage}
                  </Text>
                )}
              </BlockStack>
            )}
          </BlockStack>
        </InlineGrid>

        <Divider />

        {/* Bottom Footer */}
        <InlineStack align="space-between" blockAlign="center">
          <Text as="p" variant="bodySm" tone="subdued">
            &copy; {currentYear} {company.name}. All rights reserved.
          </Text>

          <InlineStack gap="400">
            {policyLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleLinkClick(link.href)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <Text as="span" variant="bodySm" tone="subdued">
                  {link.label}
                </Text>
              </button>
            ))}
          </InlineStack>
        </InlineStack>
      </BlockStack>
    </Box>
  );
}

// Social Icon Helper Component
function SocialIcon({ type }: { type: 'facebook' | 'twitter' | 'linkedin' | 'instagram' }) {
  const paths: Record<string, string> = {
    facebook: 'M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z',
    twitter: 'M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z',
    linkedin: 'M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z M4 6a2 2 0 100-4 2 2 0 000 4z',
    instagram: 'M7.8 2h8.4C19.4 2 22 4.6 22 7.8v8.4a5.8 5.8 0 01-5.8 5.8H7.8C4.6 22 2 19.4 2 16.2V7.8A5.8 5.8 0 017.8 2zm-.2 2A3.6 3.6 0 004 7.6v8.8C4 18.39 5.61 20 7.6 20h8.8a3.6 3.6 0 003.6-3.6V7.6C20 5.61 18.39 4 16.4 4H7.6zm9.65 1.5a1.25 1.25 0 110 2.5 1.25 1.25 0 010-2.5zM12 7a5 5 0 110 10 5 5 0 010-10zm0 2a3 3 0 100 6 3 3 0 000-6z',
  };

  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ color: 'var(--p-color-text-subdued)' }}
    >
      <path d={paths[type]} />
    </svg>
  );
}
